import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CoutChart from './CoutChart';
import './FormulairesModal.css';

const FormulairesModal = ({ 
  isOpen, 
  onClose, 
  filterType, 
  title,
  subtitle 
}) => {
  const [formulaires, setFormulaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFormulaire, setSelectedFormulaire] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isOpen && filterType) {
      fetchFormulaires();
    }
  }, [isOpen, filterType, currentPage]);

  const fetchFormulaires = async () => {
    try {
      setLoading(true);
      setError(null);

      // Construire les paramètres de filtrage selon le type
      let params = {
        page: currentPage,
        limit: itemsPerPage
      };

      switch (filterType) {
        case 'total':
          // Tous les formulaires
          break;
        case 'en_cours_exact':
          // Exactement les formulaires "en_cours" comme dans le dashboard
          params.statut = 'en_cours';
          break;
        case 'en_cours':
          // Ancien comportement: en cours + en attente
          params.statut = 'en_cours,en_attente_validation';
          break;
        case 'resolus':
          params.statut = 'valide';
          break;
        case 'rejetes':
          params.statut = 'rejete';
          break;
        case 'repartition_resolution':
          // Afficher tous les formulaires avec statut final (validé ou rejeté)
          params.statut = 'valide,rejete';
          params.sortBy = 'statut';
          params.sortOrder = 'asc';
          break;
        case 'cout_eleve':
          params.sortBy = 'cout_estime';
          params.sortOrder = 'desc';
          break;
        case 'cout_chart':
          // Pour le graphique, on récupère tous les formulaires avec coût > 0
          params.sortBy = 'cout_estime';
          params.sortOrder = 'desc';
          params.limit = 50; // Limiter pour le graphique
          break;
        default:
          break;
      }

      const response = await axios.get('/api/formulaires', { params });
      
      if (response.data.success) {
        setFormulaires(response.data.data);
        setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des formulaires:', error);
      setError('Impossible de charger les formulaires');
    } finally {
      setLoading(false);
    }
  };

  // 🔧 Fonction de décodage HTML avancée (gère le double échappement)
  const decodeHtmlEntities = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    let decoded = text;
    let previousDecoded = '';
    
    // Décodage récursif jusqu'à ce qu'il n'y ait plus de changement
    while (decoded !== previousDecoded) {
      previousDecoded = decoded;
      decoded = decoded
        // Entités multiples (double échappement)
        .replace(/&amp;amp;/g, '&')
        .replace(/&amp;quot;/g, '"')
        .replace(/&amp;#x27;/g, "'")
        .replace(/&amp;#x2F;/g, '/')
        .replace(/&amp;#x3A;/g, ':')
        .replace(/&amp;lt;/g, '<')
        .replace(/&amp;gt;/g, '>')
        // Entités simples
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#x27;/g, "'")
        .replace(/&#x2F;/g, '/')
        .replace(/&#x3A;/g, ':')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        // Entités pour caractères accentués
        .replace(/&eacute;/g, 'é')
        .replace(/&egrave;/g, 'è')
        .replace(/&ecirc;/g, 'ê')
        .replace(/&agrave;/g, 'à')
        .replace(/&acirc;/g, 'â')
        .replace(/&ccedil;/g, 'ç')
        .replace(/&ocirc;/g, 'ô')
        .replace(/&ucirc;/g, 'û')
        .replace(/&ugrave;/g, 'ù')
        .replace(/&icirc;/g, 'î')
        .replace(/&iuml;/g, 'ï');
    }
    
    return decoded;
  };

  const formatDate = (dateString) => {
    const decodedDate = decodeHtmlEntities(dateString);
    return new Date(decodedDate).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `${parseInt(amount).toLocaleString('fr-FR')}€`;
  };

  const getStatusBadge = (statut) => {
    const badges = {
      'brouillon': { bg: '#6c757d', text: '📝 Brouillon' },
      'en_cours': { bg: '#ffc107', text: '⏳ En cours' },
      'en_attente_validation': { bg: '#fd7e14', text: '⏳ En attente' },
      'valide': { bg: '#28a745', text: '✅ Validé' },
      'rejete': { bg: '#dc3545', text: '❌ Rejeté' },
      'archive': { bg: '#6c757d', text: '📦 Archivé' }
    };

    const badge = badges[statut] || { bg: '#6c757d', text: statut };
    
    return (
      <span style={{
        background: badge.bg,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500'
      }}>
        {badge.text}
      </span>
    );
  };

  const getPriorityBadge = (priorite) => {
    const priorities = {
      'faible': { bg: '#20c997', text: '🔵 Faible' },
      'normale': { bg: '#ffc107', text: '🟡 Normale' },
      'haute': { bg: '#fd7e14', text: '🟠 Haute' },
      'critique': { bg: '#dc3545', text: '🔴 Critique' }
    };

    const priority = priorities[priorite] || { bg: '#6c757d', text: priorite };
    
    return (
      <span style={{
        background: priority.bg,
        color: 'white',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500'
      }}>
        {priority.text}
      </span>
    );
  };

  const openFormulaireDetail = (formulaire) => {
    setSelectedFormulaire(formulaire);
  };

  const closeFormulaireDetail = () => {
    setSelectedFormulaire(null);
  };

  if (!isOpen) return null;

  return (
    <div className="formulaires-modal-overlay" onClick={onClose}>
      <div className="formulaires-modal" onClick={(e) => e.stopPropagation()}>
        {/* En-tête */}
        <div className="formulaires-modal-header">
          <div>
            <h3>{title}</h3>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button className="formulaires-modal-close" onClick={onClose}>×</button>
        </div>

        {/* Contenu principal */}
        <div className="formulaires-modal-content">
          {loading ? (
            <div className="formulaires-loading">
              <div className="loading-spinner"></div>
              <p>Chargement des formulaires...</p>
            </div>
          ) : error ? (
            <div className="formulaires-error">
              <h4>❌ Erreur</h4>
              <p>{error}</p>
              <button onClick={fetchFormulaires}>🔄 Réessayer</button>
            </div>
          ) : formulaires.length === 0 ? (
            <div className="formulaires-empty">
              <h4>📭 Aucun formulaire</h4>
              <p>Aucun formulaire ne correspond aux critères sélectionnés.</p>
            </div>
          ) : filterType === 'cout_chart' ? (
            /* Graphique des coûts */
            <CoutChart formulaires={formulaires} />
          ) : (
            <>
              {/* Liste des formulaires */}
              <div className="formulaires-list">
                {formulaires.map((formulaire) => (
                  <div 
                    key={formulaire.id} 
                    className="formulaire-item"
                    onClick={() => openFormulaireDetail(formulaire)}
                  >
                    <div className="formulaire-header">
                      <div className="formulaire-title">
                        <strong>{decodeHtmlEntities(formulaire.equipement_zone_ligne)}</strong>
                        <span className="formulaire-id">#{formulaire.id.slice(0, 8)}</span>
                      </div>
                      <div className="formulaire-badges">
                        {getStatusBadge(formulaire.statut)}
                        {getPriorityBadge(formulaire.priorite)}
                      </div>
                    </div>
                    
                    <div className="formulaire-problem">
                      <span className="problem-label">Problème:</span>
                      <span className="problem-text">{decodeHtmlEntities(formulaire.probleme_initial || '')}</span>
                    </div>
                    
                    <div className="formulaire-meta">
                      <div className="meta-item">
                        <span className="meta-label">👤 Créé par:</span>
                        <span>{formulaire.noms}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">📅 Date:</span>
                        <span>{formatDate(formulaire.created_at)}</span>
                      </div>
                      <div className="meta-item">
                        <span className="meta-label">💰 Coût:</span>
                        <span className="cost-value">{formatCurrency(formulaire.cout_estime)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="formulaires-pagination">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    ← Précédent
                  </button>
                  
                  <span className="pagination-info">
                    Page {currentPage} sur {totalPages}
                  </span>
                  
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Suivant →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modale de détail du formulaire */}
      {selectedFormulaire && (
        <div className="formulaire-detail-overlay" onClick={closeFormulaireDetail}>
          <div className="formulaire-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="formulaire-detail-header">
              <h3>📋 Détail du Formulaire</h3>
              <button onClick={closeFormulaireDetail}>×</button>
            </div>
            
            <div className="formulaire-detail-content">
              <div className="detail-section">
                <h4>🏭 Équipement</h4>
                <p><strong>{selectedFormulaire.equipement_zone_ligne}</strong></p>
              </div>
              
              <div className="detail-section">
                <h4>⚠️ Problème</h4>
                <p>{selectedFormulaire.probleme_initial}</p>
              </div>
              
              {selectedFormulaire.description_phenomene && (
                <div className="detail-section">
                  <h4>📝 Description</h4>
                  <p>{selectedFormulaire.description_phenomene}</p>
                </div>
              )}
              
              {selectedFormulaire.actions_entreprise && (
                <div className="detail-section">
                  <h4>🔧 Actions Entreprises</h4>
                  <p>{selectedFormulaire.actions_entreprise}</p>
                </div>
              )}
              
              <div className="detail-section">
                <h4>📊 Informations</h4>
                <div className="detail-grid">
                  <div><strong>Statut:</strong> {getStatusBadge(selectedFormulaire.statut)}</div>
                  <div><strong>Priorité:</strong> {getPriorityBadge(selectedFormulaire.priorite)}</div>
                  <div><strong>Coût estimé:</strong> {formatCurrency(selectedFormulaire.cout_estime)}</div>
                  <div><strong>Créé le:</strong> {formatDate(selectedFormulaire.created_at)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulairesModal;
